<template>
  <div class="migration-progress-container">
    <img src="../../../assets/img/MigrationInProgress.png" alt="Migration in Progress" width="300" class="migrationProgress">
    <div class="migration-progress-overlay">
      <div class="progress-checklist">
        <div
          v-for="step in steps"
          :key="step.number"
          class="progress-item"
          :class="{
            'completed': step.number < shop.upgrade_process_step,
            'current': shop.upgrade_process_step === step.number
          }"
        >
          <div class="progress-icon">
            <i v-if="step.number < shop.upgrade_process_step" class="fas fa-check"></i>
            <i v-else-if="shop.upgrade_process_step === step.number" class="fas fa-arrow-right"></i>
            <span v-else class="step-number">{{ step.number }}</span>
          </div>
          <div class="progress-content">
            <div class="progress-title">{{ step.title }}</div>
            <div class="progress-description" v-html="shop.upgrade_process_step > step.number ? step.completedText : getStepText(step)">
            </div>
            <button
              v-if="step.number === 5 && shop.upgrade_process_step === 5"
              class="enable-btn"
              @click="$emit('activate')"
            >
              Activate
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MigrationProgress',
  props: {
    shop: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      steps: [
        {
          number: 1,
          title: 'Offer Secured',
          completedText: 'You\'ve locked in your discount!',
          pendingText: 'Lock in your exclusive pricing'
        },
        {
          number: 2,
          title: 'Permissions Updated',
          completedText: 'App permissions granted!',
          pendingText: '<strong>Action needed</strong> – grant new app access'
        },
        {
          number: 3,
          title: 'Setup & Data Migrated',
          completedText: 'Migration completed by our team!',
          pendingText: 'We are working on this step'
        },
        {
          number: 4,
          title: 'App Access & Review',
          completedText: 'Access and review completed!',
          pendingText: '<strong>Action needed</strong> – <a href="https://b.visceralapps.com/login/" target="_blank" style="color:#fff;font-weight:normal;text-decoration:underline">test and review your new app</a> to move forward (live walkthrough available)'
        },
        {
          number: 5,
          title: 'New App Activation',
          completedText: "Activation confirmed – we'll finalize your upgrade right away!",
          pendingText: 'Final step – confirm activation to go live'
        }
      ]
    }
  },
  methods: {
    getStepText (step) {
      if (step.number === 4 && this.shop.upgrade_process_step === 4) {
        return '<strong>Action needed</strong> – <a href="https://b.visceralapps.com/login/" target="_blank" style="color:#fff;font-weight:normal;text-decoration:underline">test and review your new app</a> to move forward (live walkthrough available)'
      }
      if (step.number === 4) {
        return '<strong>Action needed</strong> – test and review your new app to move forward (live walkthrough available)'
      }
      return step.pendingText
    }
  }
}
</script>

<style lang="scss" scoped>
.migration-progress-container {
  position: relative;
  display: inline-block;
  background-color: #261c32 !important;
}

.migration-progress-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 12px;
  color: #fff;
  min-height: 270px;

  .progress-checklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .progress-item {
    display: flex;
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.3;

    &.completed {
      .progress-icon {
        background: #28a745;
        color: white;
        border: 2px solid #28a745;
      }
      .progress-title {
        color: #fff;
        font-weight: 600;
      }
      .progress-description {
        color: rgba(255, 255, 255, 0.9);
      }
    }

    &.current {
      .progress-icon {
        background: transparent;
        color: #fff;
        border: 2px solid #fff;
        position: relative;
        animation: pulse 2s infinite;

        .fas.fa-arrow-right {
          font-size: 10px;
          line-height: 1;
        }
      }
      .progress-title {
        color: rgba(255, 255, 255, 0.9);
        font-weight: 700;
      }
      .progress-description {
        color: rgba(255, 255, 255, 0.9);
        font-weight: 500;
      }
    }

    &:not(.completed):not(.current) {
      .progress-icon {
        background: transparent;
        color: rgba(255, 255, 255, 0.9);
        border: 2px solid rgba(255, 255, 255, 0.3);
      }
      .progress-title {
        color: rgba(255, 255, 255, 0.7);
      }
      .progress-description {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  .progress-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    margin-top: 2px;
    font-size: 10px;
    font-weight: bold;
    flex-shrink: 0;

    .fas.fa-check {
      font-size: 10px;
      line-height: 1;
    }

    .step-number {
      display: none;
    }
  }

  .progress-content {
    flex: 1;
    min-width: 0;
  }

  .progress-title {
    font-weight: 600;
    margin-bottom: 2px;
    font-size: 16px;
    line-height: 1.2;
  }

  .progress-description {
    font-size: 11px;
    line-height: 1.3;
    margin-bottom: 3px;
  }

  .enable-btn {
    background: var(--primary-color, #eb008c);
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 10px;
    cursor: pointer;
    margin-top: 4px;
    font-weight: 600;

    &:hover {
      opacity: 0.9;
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/vendorconsignment/components/MigrationProgress.vue